/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React from 'react';

import { useAppDispatch } from '../../../../app/hooks';
import { FhirPageWidgetType, PageOptionExclusiveType, PageOptionMultipleChoiceType } from '../../algorithm/types';
import { setFhirHighlight } from '../../Fhir/fhirSlice';
import { useUpdateButtonFhirSidebar } from '../../Fhir/useHooksFhirSidebar';
import { useMessageIndex, usePageId } from '../hooks/MessageContext';
import { ButtonFhirIcon } from './ButtonFhirIcon';
import styles from './OptionsButton.module.scss';
import { TooltipIcon } from './TooltipIcon';
import { ButtonEMIcon } from './ButtonEMIcon';
import clsx from 'clsx';

type OptionsButtonProps = {
  onClick?: () => void;
  children: React.ReactNode;
  style?: React.CSSProperties;
  option: PageOptionExclusiveType | PageOptionMultipleChoiceType | undefined;
  forceShowTooltip?: boolean;
  onTooltipClick?: (element: HTMLElement, message: string, header: string) => void;
  fhir?: FhirPageWidgetType;
  forceShowFhir?: boolean;
  onFhirClick?: () => void;
  className?: string;
  selected?: boolean;
  questionIndex: number;
  optionIndex: number;
  points?: number;
  enableEMHighlight?: boolean;
  buttonKey?: string; // Just for debugging etc
  buttonValue?: string; // Just for debugging etc
  isDisabled?: boolean;
  onEMHighlightToggle?: (showEMHighlight: boolean) => void;
  documentVersionId?: string;
  showLinks?: boolean; // In creator Algorithm flat view, we want to show the user where this links to
  inEditorMode?: boolean;
};

export function OptionsButton({
  className,
  children,
  option,
  onClick,
  onTooltipClick,
  points,
  selected,
  style,
  forceShowTooltip,
  fhir,
  forceShowFhir,
  onFhirClick,
  questionIndex,
  optionIndex,
  buttonKey,
  buttonValue,
  isDisabled,
  enableEMHighlight,
  onEMHighlightToggle,
  documentVersionId,
  showLinks, // In creator Algorithm flat view, we want to show the user where this links to
  inEditorMode = false,
}: OptionsButtonProps) {
  const { fhirWidgetId, tooltip, isNone, text: tooltipHeader, showEMHighlight } = option || {};
  // messageIndex is just for automated testing
  const messageIndex = useMessageIndex();
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const pageId = usePageId();
  const dispatch = useAppDispatch();
  useUpdateButtonFhirSidebar(buttonRef, fhir, pageId, fhirWidgetId, documentVersionId, option?.text, inEditorMode);
  return (
    <div
      className={clsx(
        styles.option,
        selected && styles.option_selected,
        isNone && styles.none_option,
        className,
        isDisabled && styles.disabled_option,
        'options_button',
        fhir && !!fhir.predefinedWidgets.length ? 'fhir-text' : '',
        showEMHighlight && styles.option_emhighlight,
        showLinks && styles.option_showlinks
      )}
      style={style}
      ref={buttonRef}
      data-message-index={messageIndex}
      data-question-index={questionIndex}
      data-option-index={optionIndex}
      data-key={buttonKey} /* Just for debugging etc */
      data-value={buttonValue} /* Just for debugging etc */
      data-fhir-widget-id={
        fhir?.predefinedWidgets.map((pw) => pw.id).join(',') ?? ''
      } /* Deprecated - edit deprecated why? */
      data-id={fhirWidgetId}
      id={fhirWidgetId}
      data-selected={selected ? 'true' : 'false'}
      data-em-highlight-toggle={enableEMHighlight && fhirWidgetId ? 'true' : 'false'}
      onMouseEnter={() => {
        if (fhirWidgetId) {
          dispatch(
            setFhirHighlight({
              messageIndex,
              fhirWidgetIds: [fhirWidgetId],
              predefinedWidgetIds: fhir?.predefinedWidgets.map((pw) => pw.id) || [],
              hoveringOverWidget: false,
              documentVersionId,
              pageId,
              inEditorMode: true,
            })
          );
        }
      }}
      onMouseLeave={() => dispatch(setFhirHighlight(undefined))}
      onClick={() => !isDisabled && onClick && onClick()}
    >
      {children}

      {points ? <span className={styles.points}>{points > 0 ? `+${points}` : points}</span> : ''}
      {fhirWidgetId && (
        <TooltipIcon
          className={styles.tooltip}
          onTooltipClick={onTooltipClick}
          pageId={pageId}
          tooltip={tooltip}
          tooltipHeader={tooltipHeader}
          forceShowTooltip={forceShowTooltip}
        />
      )}
      {forceShowFhir && fhirWidgetId && (
        <ButtonFhirIcon
          className={styles.fhirIcon}
          onFhirClick={onFhirClick}
          pageId={pageId}
          fhir={fhir}
          forceShowFhir={forceShowFhir}
        />
      )}
      {/* In edit ,we show a small icon to toggle whether we want to show the EM highlight or not. */}
      {enableEMHighlight && onEMHighlightToggle && !isNone && fhirWidgetId && (
        <ButtonEMIcon showEMHighlight={!!showEMHighlight} setShowEMHighlight={onEMHighlightToggle} />
      )}
      {/* In creator Algorithm flat view, we want to show the user where this links to */}
      {showLinks && option?.link ? (
        <div className={styles.optionLinkInfo}>
          ↪️ links to {option.link.startsWith('#') ? 'document' : 'page'}:<br /> <b>{option.link}</b>
          {option.linkSlug ? (
            <code> (🐌&nbsp;{option.linkSlug})</code>
          ) : option.link.startsWith('#') ? (
            <code> (🐌&nbsp;missing&nbsp;slug)</code>
          ) : (
            ''
          )}
        </div>
      ) : undefined}
    </div>
  );
}
